import { useRef, useEffect } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Calendar, MapPin, ExternalLink } from "lucide-react";

interface VenueSectionProps {
  dateTime: string | null;
  location: string | null;
  /**
   * Doluysa bölümün üstünde etkinlik adı rozeti çıkar ("KINA"). Aynı bölüm
   * ikinci etkinlik için ikinci kez render edildiğinde iki bloğu ayırır.
   */
  eventLabel?: string;
}

export function VenueSection({ dateTime, location, eventLabel }: VenueSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const colsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!colsRef.current) return;
    gsap.fromTo(
      colsRef.current.children,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  if (!dateTime && !location) return null;

  const date = dateTime ? new Date(dateTime) : null;
  const formattedDate = date
    ? date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : null;
  const formattedTime = date
    ? date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
    : null;

  const mapsUrl = location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    : null;

  return (
    <section
      ref={sectionRef}
      className="min-h-screen px-6 py-24"
      style={{
        background: "linear-gradient(180deg, #1b1817 0%, #2d1f1a 30%, #f7f7f7 100%)",
      }}
    >
      <div className="mx-auto max-w-4xl">
        {eventLabel && (
          <p className="mb-10 text-center text-xs font-medium tracking-[0.4em] text-[#8d6b5e] uppercase">
            {eventLabel}
          </p>
        )}
        <div ref={colsRef} className="grid gap-12 md:grid-cols-2 md:gap-16">
          {dateTime && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-[#8d6b5e]" />
                <span className="text-xs font-medium tracking-[0.3em] text-white/50 uppercase md:text-[#1b1817]/50">
                  Tarih & Saat
                </span>
              </div>
              <div>
                <p
                  className="font-serif text-3xl font-bold text-white md:text-[#1b1817] sm:text-4xl"
                  style={{ fontFamily: "var(--font-serif, 'Playfair Display', serif)" }}
                >
                  {formattedDate}
                </p>
                {formattedTime && (
                  <p className="mt-2 text-lg text-white/60 md:text-[#1b1817]/60">
                    Saat {formattedTime}
                  </p>
                )}
              </div>
            </div>
          )}

          {location && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-[#8d6b5e]" />
                <span className="text-xs font-medium tracking-[0.3em] text-white/50 uppercase md:text-[#1b1817]/50">
                  Mekan
                </span>
              </div>
              <div>
                <p
                  className="font-serif text-3xl font-bold text-white md:text-[#1b1817] sm:text-4xl"
                  style={{ fontFamily: "var(--font-serif, 'Playfair Display', serif)" }}
                >
                  {location}
                </p>
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#8d6b5e] hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Google Maps'te Aç
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
